import { useEffect, useRef } from "react"

import type { GPTModelItem } from "@/api/models"
import type { Project } from "@/services/project-service"

type UseProjectDefaultsArgs = {
  activeProject?: Project
  allowWebSearch: boolean
  // Current (pre-project) values — read so we can snapshot them before a
  // project's defaults are applied, and restore them when the user leaves
  // the project. Without these we'd have no way to know what to revert to.
  enableWebSearch: boolean
  handleWebSearchChange: (enabled: boolean) => void
  availableModels: GPTModelItem[]
  currentGptModel?: GPTModelItem
  // True only after the user's persisted model settings have loaded. Before
  // that, currentGptModel can be an "auto"/first-model fallback rather than
  // the user's real baseline.
  modelSettingsHydrated: boolean
  // Change base settings
  handleGptModelChange: (model: GPTModelItem) => void
  // Research preferences
  sourceUrls: string
  setSourceUrls: (val: string) => void
  trustedMode: "only" | "preferred"
  setTrustedMode: (val: "only" | "preferred") => void
}

export function useProjectDefaults({
  activeProject,
  allowWebSearch,
  enableWebSearch,
  handleWebSearchChange,
  availableModels,
  currentGptModel,
  modelSettingsHydrated,
  handleGptModelChange,
  sourceUrls,
  setSourceUrls,
  trustedMode,
  setTrustedMode,
}: UseProjectDefaultsArgs) {
  // Store callbacks and values in refs to avoid re-triggering the effect
  // when unstable function references change between renders
  const handleWebSearchChangeRef = useRef(handleWebSearchChange)
  handleWebSearchChangeRef.current = handleWebSearchChange
  const handleGptModelChangeRef = useRef(handleGptModelChange)
  handleGptModelChangeRef.current = handleGptModelChange
  const availableModelsRef = useRef(availableModels)
  availableModelsRef.current = availableModels
  const setSourceUrlsRef = useRef(setSourceUrls)
  setSourceUrlsRef.current = setSourceUrls
  const setTrustedModeRef = useRef(setTrustedMode)
  setTrustedModeRef.current = setTrustedMode

  // Latest current-value refs, used only to capture a snapshot right
  // before we first apply a project's defaults — never as effect deps.
  const enableWebSearchRef = useRef(enableWebSearch)
  enableWebSearchRef.current = enableWebSearch
  const sourceUrlsRef = useRef(sourceUrls)
  sourceUrlsRef.current = sourceUrls
  const trustedModeRef = useRef(trustedMode)
  trustedModeRef.current = trustedMode
  const currentGptModelRef = useRef(currentGptModel)
  currentGptModelRef.current = currentGptModel

  // Tracks which project's web-search/trusted-source defaults are currently
  // applied, so we (a) only apply them once per project entry — not on every
  // refetch/identity change of the same project — and (b) know when to
  // revert on exit.
  const appliedSearchProjectIdRef = useRef<string | undefined>(undefined)
  const searchSnapshotRef = useRef<{
    webSearch: boolean
    sourceUrls: string
    trustedMode: "only" | "preferred"
  } | null>(null)

  // Same idea, scoped to the default-model application.
  const appliedModelProjectIdRef = useRef<string | undefined>(undefined)
  const modelSnapshotRef = useRef<GPTModelItem | undefined>(undefined)

  // Effect 1: Apply (and revert) web search and trusted source defaults
  useEffect(() => {
    const projectId = activeProject?.id

    if (!activeProject) {
      // Left the project (or never entered one) — restore whatever was in
      // effect before we applied this project's defaults.
      if (searchSnapshotRef.current) {
        const snapshot = searchSnapshotRef.current
        handleWebSearchChangeRef.current(snapshot.webSearch)
        setSourceUrlsRef.current(snapshot.sourceUrls)
        setTrustedModeRef.current(snapshot.trustedMode)
      }
      appliedSearchProjectIdRef.current = undefined
      searchSnapshotRef.current = null
      return
    }

    // Capture the true non-project baseline before either the permission gate
    // or project defaults can change it. Keep this snapshot across direct
    // project-to-project navigation.
    if (searchSnapshotRef.current === null) {
      searchSnapshotRef.current = {
        webSearch: enableWebSearchRef.current,
        sourceUrls: sourceUrlsRef.current,
        trustedMode: trustedModeRef.current,
      }
    }

    if (!allowWebSearch) {
      // Permission gate — always enforce web search off. Keep appliedSearchProjectIdRef
      // when defaults were already applied so a later permission grant does not
      // re-run project defaults and clobber mid-session manual overrides. When
      // permission arrives before the first apply, the ref stays unset and the
      // block below still runs the one-time default application.
      handleWebSearchChangeRef.current(false)
      return
    }

    if (appliedSearchProjectIdRef.current === projectId) {
      // Defaults already applied for this project entry — don't reapply on
      // every refetch (e.g. after a mutation), which would clobber the
      // user's manual overrides made mid-session.
      return
    }

    // Every new project derives from the original non-project snapshot,
    // never from the previous project's current values. This also restores
    // fields that the new project leaves unspecified.
    const snapshot = searchSnapshotRef.current
    appliedSearchProjectIdRef.current = projectId

    handleWebSearchChangeRef.current(
      typeof activeProject.defaultWebSearch === "boolean"
        ? activeProject.defaultWebSearch
        : snapshot.webSearch
    )

    // Collect trusted source URLs from both new format (trustedSources) and legacy format (trustedSourceUrls)
    const allUrls: string[] = []

    // Add URLs from new format (trustedSources array of {name, url} objects)
    if (Array.isArray(activeProject.trustedSources)) {
      activeProject.trustedSources.forEach((source) => {
        if (source.url) allUrls.push(source.url)
      })
    }

    // Add URLs from legacy format (trustedSourceUrls array of strings)
    // Only add URLs that aren't already included from the new format
    if (Array.isArray(activeProject.trustedSourceUrls)) {
      const existingUrls = new Set(allUrls)
      activeProject.trustedSourceUrls.forEach((url) => {
        if (url && !existingUrls.has(url)) allUrls.push(url)
      })
    }

    // Apply collected URLs
    if (allUrls.length > 0) {
      setSourceUrlsRef.current(allUrls.join("\n"))
      setTrustedModeRef.current("only")
    } else {
      setSourceUrlsRef.current(snapshot.sourceUrls)
      setTrustedModeRef.current(snapshot.trustedMode)
    }
  }, [activeProject, allowWebSearch])

  // Effect 2: Apply (and revert) default model when project or available
  // models change. Uses availableModels.length (stable primitive) instead
  // of the array reference to avoid infinite re-renders from unstable array
  // identity. The project's default model is never allowed to permanently
  // replace the user's saved model preference — it's restored on exit.
  useEffect(() => {
    const projectId = activeProject?.id

    if (!activeProject) {
      if (modelSnapshotRef.current) {
        handleGptModelChangeRef.current(modelSnapshotRef.current)
      }
      appliedModelProjectIdRef.current = undefined
      modelSnapshotRef.current = undefined
      return
    }

    if (appliedModelProjectIdRef.current === projectId) {
      // Already applied (or already attempted) for this project entry.
      return
    }

    if (!activeProject.defaultModelId) {
      // This project has no default model — revert any override still
      // applied from a previously visited project (chained A -> B switches)
      // so it doesn't leak into this project's session.
      if (modelSnapshotRef.current) {
        handleGptModelChangeRef.current(modelSnapshotRef.current)
      }
      appliedModelProjectIdRef.current = projectId
      modelSnapshotRef.current = undefined
      return
    }

    // currentGptModel is a fallback ("auto" or the first available model)
    // until user settings hydrate. Snapshotting or persisting a project
    // override before then loses the user's real baseline.
    if (!modelSettingsHydrated || !currentGptModelRef.current) {
      return
    }

    const model = availableModelsRef.current.find((m) => m.id === activeProject.defaultModelId)
    if (model) {
      // Only snapshot when there isn't already a project override in
      // effect (see effect 1 above for why chained project -> project
      // switches must not re-capture the already-overridden current value).
      if (!modelSnapshotRef.current) {
        modelSnapshotRef.current = currentGptModelRef.current
      }
      appliedModelProjectIdRef.current = projectId
      handleGptModelChangeRef.current(model)
    }
  }, [activeProject, availableModels.length, modelSettingsHydrated])

  // Effect 3: Restore project defaults on unmount (cross-module exit).
  //
  // The in-module restore branches in effects 1 & 2 only fire when
  // activeProject transitions to undefined while this hook stays mounted.
  // Navigating to a *different* module unmounts AIExplorerPage without such a
  // transition, so those branches never run — leaving the project's
  // sourceUrls/trustedMode pinned in the localStorage-persisted research-
  // preferences store and the project's model persisted in the user's DB
  // settings. This cleanup mirrors those restore paths so the captured
  // baseline is put back on the way out.
  //
  // Empty deps => the returned cleanup runs only on unmount. It reads and
  // resets the same refs the in-module restores mutate, so if an in-module
  // restore already ran (refs cleared) this is a no-op, and it can never run
  // twice — the two paths never fight.
  useEffect(() => {
    return () => {
      // Search prefs baseline (persisted only to the local store — cheap).
      if (searchSnapshotRef.current) {
        const snapshot = searchSnapshotRef.current
        handleWebSearchChangeRef.current(snapshot.webSearch)
        setSourceUrlsRef.current(snapshot.sourceUrls)
        setTrustedModeRef.current(snapshot.trustedMode)
        appliedSearchProjectIdRef.current = undefined
        searchSnapshotRef.current = null
      }

      // Model baseline — always issue the restore intent when an override was
      // applied. The optimistic settings cache may not have rerendered this
      // hook before unmount, so currentGptModelRef can still equal the baseline
      // even while the project PATCH is in flight.
      if (modelSnapshotRef.current) {
        handleGptModelChangeRef.current(modelSnapshotRef.current)
      }
      appliedModelProjectIdRef.current = undefined
      modelSnapshotRef.current = undefined
    }
    // Refs are stable; intentionally run this cleanup only on unmount.
  }, [])
}
